/**
 * Parses a WebVTT timestamp string (e.g. "00:01:23.456" or "01:23.456") to seconds.
 *
 * @param {string} timestampStr - The timestamp string
 * @returns {number|null} Total seconds as float, or null if invalid
 */
export const parseVttTimestamp = (timestampStr) => {
  if (!timestampStr || typeof timestampStr !== 'string') {
    return null;
  }

  const trimmed = timestampStr.trim().replace(',', '.');
  const parts = trimmed.split(':');

  if (parts.length === 2) {
    const minutes = parseFloat(parts[0]);
    const seconds = parseFloat(parts[1]);
    if (isNaN(minutes) || isNaN(seconds) || minutes < 0 || seconds < 0) {
      return null;
    }
    return minutes * 60 + seconds;
  }

  if (parts.length === 3) {
    const hours = parseFloat(parts[0]);
    const minutes = parseFloat(parts[1]);
    const seconds = parseFloat(parts[2]);
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds) || hours < 0 || minutes < 0 || seconds < 0) {
      return null;
    }
    return hours * 3600 + minutes * 60 + seconds;
  }

  return null;
};

/**
 * Formats a time in seconds to a human-readable display string (MM:SS or HH:MM:SS).
 *
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted timestamp string (e.g. "00:00", "01:23", "01:02:03")
 */
export const formatTime = (seconds) => {
  if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) {
    return '00:00';
  }

  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  const pad = (num) => String(num).padStart(2, '0');

  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  }
  return `${pad(minutes)}:${pad(secs)}`;
};

/**
 * Parses WebVTT chapter string into structured chapter cues.
 *
 * @param {string} vttText - Raw WebVTT content
 * @returns {Array<{id: string|number, startTime: number, endTime: number, text: string}>} Array of chapter cues
 */
export const parseVttChapters = (vttText) => {
  if (!vttText || typeof vttText !== 'string') {
    return [];
  }

  const lines = vttText.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const chapters = [];

  let i = 0;
  // Skip WEBVTT header line and optional header description
  while (i < lines.length && (lines[i].trim().startsWith('WEBVTT') || lines[i].trim() === '')) {
    i++;
  }

  let cueId = null;
  let cueStartTime = null;
  let cueEndTime = null;
  let cueTextLines = [];
  let inNoteOrStyle = false;

  const timestampRegex = /((?:\d{2}:)?\d{2}:\d{2}[.,]\d{3}|\d{2}:\d{2}[.,]\d{3}|(?:\d{2}:)?\d{2}:\d{2})\s*-->\s*((?:\d{2}:)?\d{2}:\d{2}[.,]\d{3}|\d{2}:\d{2}[.,]\d{3}|(?:\d{2}:)?\d{2}:\d{2})/;

  const flushCue = () => {
    if (cueStartTime !== null && cueEndTime !== null) {
      const text = cueTextLines.join(' ').replace(/<[^>]+>/g, '').trim();
      const id = cueId || `chapter-${chapters.length + 1}`;
      chapters.push({
        id,
        startTime: cueStartTime,
        endTime: cueEndTime,
        text: text || `Chapter ${chapters.length + 1}`
      });
    }
    cueId = null;
    cueStartTime = null;
    cueEndTime = null;
    cueTextLines = [];
  };

  for (; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line === '') {
      inNoteOrStyle = false;
      flushCue();
      continue;
    }

    if (line.startsWith('NOTE') || line.startsWith('STYLE')) {
      inNoteOrStyle = true;
      continue;
    }

    if (inNoteOrStyle) {
      continue;
    }

    const timeMatch = line.match(timestampRegex);
    if (timeMatch) {
      cueStartTime = parseVttTimestamp(timeMatch[1]);
      cueEndTime = parseVttTimestamp(timeMatch[2]);
    } else if (cueStartTime === null) {
      // Line before timestamp can be cue identifier
      cueId = line;
    } else {
      // Line after timestamp is cue text
      cueTextLines.push(line);
    }
  }

  flushCue();

  return chapters.filter((c) => c.startTime !== null && c.endTime !== null && !isNaN(c.startTime));
};
